import { Howl, Howler } from 'howler'

export interface AudioManagerState {
  currentSrc: string | null
  isPlaying: boolean
  isLoading: boolean
  currentTime: number
  duration: number
}

type StateChangeListener = (state: AudioManagerState) => void

class AudioManager {
  private static instance: AudioManager
  private currentAudio: Howl | null = null
  private currentSrc: string | null = null
  private _isLoading: boolean = false
  private _isPlaying: boolean = false
  private _currentTime: number = 0
  private _duration: number = 0
  private rafId: number | null = null // For requestAnimationFrame

  private listeners: StateChangeListener[] = []

  private constructor() {
    Howler.volume(1) // Set default global volume
  }

  public static getInstance(): AudioManager {
    if (!AudioManager.instance) {
      AudioManager.instance = new AudioManager()
    }
    return AudioManager.instance
  }

  public subscribe(listener: StateChangeListener): () => void {
    this.listeners.push(listener)
    // Initial state push
    listener({
      currentSrc: this.currentSrc,
      isPlaying: this._isPlaying,
      isLoading: this._isLoading,
      currentTime: this._currentTime,
      duration: this._duration,
    })
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener)
    }
  }

  // --- Playback Controls ---
  public play(src: string, startTime?: number): void {
    if (this.currentSrc === src && this._isPlaying) return

    if (this.currentAudio && this.currentSrc === src && !this._isPlaying) {
      console.debug(`Resuming: ${src}`)

      // If startTime is provided, seek to that position before resuming
      if (startTime !== undefined) {
        console.debug(`Setting start time to: ${startTime}s`)
        this.currentAudio.seek(startTime)
      }

      this.currentAudio.volume(1) // Set volume directly instead of fading
      this.currentAudio.play()
      // isPlaying state, and RAF loop will be handled by onplay/tick
      return
    }

    // Stop previous audio immediately
    this.stopRaf()
    const previousAudio = this.currentAudio
    const previousSrc = this.currentSrc

    if (previousAudio) {
      console.debug(`Stopping previous: ${previousSrc}`)
      this.setState({ currentTime: 0, duration: 0 }) // Reset state immediately
      previousAudio.stop()
      previousAudio.unload()
    } else {
      this.setState({ currentTime: 0, duration: 0 }) // Reset time/duration if nothing was playing
    }

    // --- Load and play the new audio ---
    console.debug(`Loading and playing: ${src}`)
    this.currentSrc = src // Set currentSrc immediately
    this.setState({
      isLoading: true,
      currentSrc: src,
      isPlaying: false,
      currentTime: startTime !== undefined ? startTime : 0,
      duration: 0,
    })

    this.currentAudio = new Howl({
      src: [src],
      volume: 1,
      html5: true,
      onload: () => {
        console.debug(`Loaded: ${src}`)
        if (this.currentSrc === src) {
          const duration = this.currentAudio?.duration() || 0
          this.setState({ isLoading: false, duration: duration })

          // If startTime is provided, seek to that position before playing
          if (startTime !== undefined && this.currentAudio) {
            console.debug(`Setting start time to: ${startTime}s`)
            this.currentAudio.seek(startTime)
            // Update state to reflect the new position
            this.setState({ currentTime: startTime })
          }

          console.debug(`Playing loaded track: ${src}`)
          this.currentAudio?.play()
          // State update for playing happens in onplay
        } else {
          console.debug(`Load complete for ${src}, but abandoned.`)
          this.currentAudio?.unload()
          if (!this._isPlaying && !this._isLoading) {
            this.setState({ isLoading: false })
          }
        }
      },
      onplay: () => {
        console.debug(`Playing: ${src}`)
        if (this.currentSrc === src) {
          const duration = this.currentAudio?.duration() || 0
          this.setState({ isPlaying: true, isLoading: false, duration: duration })
          this.startRaf() // Start updating time
        }
      },
      onpause: () => {
        console.debug(`Paused: ${src}`)
        if (this.currentSrc === src) {
          this.setState({ isPlaying: false })
          this.stopRaf() // Stop updating time
        }
      },
      onstop: () => {
        console.debug(`Stopping: ${src}`)
        if (this.currentSrc === src) {
          this.setState({ isPlaying: false, currentSrc: null, currentTime: 0 })
          this.currentAudio = null
          this.currentSrc = null
          this.stopRaf()
        }
      },
      onend: () => {
        console.debug(`Ended: ${src}`)
        if (this.currentSrc === src) {
          this.setState({ isPlaying: false, currentSrc: null, currentTime: 0 })
          this.currentAudio = null
          this.currentSrc = null
          this.stopRaf()
        }
      },
      // Add onseek handler - this updates state when Howler finishes seeking
      onseek: () => {
        if (this.currentAudio && this.currentSrc === src) {
          const currentSeek = this.currentAudio.seek() || 0
          console.debug(`Seek complete, updating state time to ${currentSeek.toFixed(2)}`)
          // Update state after seek operation completes internally in Howler
          this.setState({ currentTime: currentSeek })
        }
      },
      onloaderror: (id, error) => this.handleError(src, 'Load error', error),
      onplayerror: (id, error) => this.handleError(src, 'Play error', error),
    })
  }

  public pause(): void {
    if (this.currentAudio && this._isPlaying) {
      console.debug(`Pausing: ${this.currentSrc}`)
      this.stopRaf()
      this.currentAudio.pause()
    }
  }

  public stop(): void {
    if (this.currentAudio) {
      const audioToStop = this.currentAudio
      const srcToStop = this.currentSrc
      console.debug(`Stopping: ${srcToStop}`)
      this.stopRaf()

      this.currentAudio = null
      this.currentSrc = null
      this.setState({ isPlaying: false, currentSrc: null, currentTime: 0 }) // Immediate state reset

      console.debug(`Stopping immediately: ${srcToStop}`)
      audioToStop.stop()
      audioToStop.unload()
    } else {
      this.setState({ isPlaying: false, currentSrc: null, currentTime: 0, duration: 0 })
    }
  }

  /**
   * Seeks the currently loaded audio track to the specified time.
   * @param time The time in seconds to seek to.
   */
  public seek(time: number): void {
    if (!this.currentAudio) {
      console.warn('Cannot seek: No audio loaded.')
      return
    }

    const duration = this.currentAudio.duration()
    // Check if duration is valid (a positive finite number)
    if (duration === null || !Number.isFinite(duration) || duration <= 0) {
      console.warn(`Cannot seek: Duration not available or invalid (${duration}).`)
      return
    }

    // Clamp the time between 0 and duration
    const clampedTime = Math.max(0, Math.min(time, duration))

    // Ensure the clamped time is also valid before seeking
    if (!Number.isFinite(clampedTime)) {
      console.warn(`Cannot seek: Invalid time value provided or calculated (${time} -> ${clampedTime}).`)
      return
    }

    console.debug(`Seeking to: ${clampedTime.toFixed(2)}s (Duration: ${duration.toFixed(2)}s)`)
    this.currentAudio.seek(clampedTime)
    this.setState({ currentTime: clampedTime })
  }

  // --- Global Controls & Cleanup ---
  public setGlobalVolume(volume: number): void {
    Howler.volume(Math.max(0, Math.min(1, volume)))
  }

  public cleanup(): void {
    console.debug('AudioManager cleanup')
    this.stop()
    this.listeners = []
    // Howler.unload();
  }

  public getState(): AudioManagerState {
    return {
      currentSrc: this.currentSrc,
      isPlaying: this._isPlaying,
      isLoading: this._isLoading,
      currentTime: this._currentTime,
      duration: this._duration,
    }
  }

  // --- State Management ---
  private setState(
    newState: Partial<Omit<AudioManagerState, 'currentTime' | 'duration'>> & {
      currentTime?: number
      duration?: number
    }
  ) {
    // Update internal keepers
    if (newState.currentSrc !== undefined) this.currentSrc = newState.currentSrc
    if (newState.isPlaying !== undefined) this._isPlaying = newState.isPlaying
    if (newState.isLoading !== undefined) this._isLoading = newState.isLoading
    if (newState.currentTime !== undefined) this._currentTime = newState.currentTime
    if (newState.duration !== undefined) this._duration = newState.duration

    // Construct the full state to emit
    const fullState: AudioManagerState = {
      currentSrc: this.currentSrc,
      isPlaying: this._isPlaying,
      isLoading: this._isLoading,
      currentTime: this._currentTime,
      duration: this._duration,
    }

    // Notify listeners
    this.listeners.forEach((listener) => listener(fullState))
  }

  // --- Time Update Logic ---
  private tick = () => {
    if (!this.currentAudio || !this._isPlaying) {
      this.stopRaf()
      return
    }

    // Get seek returns the current playback position
    const seek = this.currentAudio.seek() || 0
    // Ensure duration is updated if it changes (can happen with some streams)
    const duration = this.currentAudio.duration() || 0

    // Check against internal state keepers before updating
    if (seek !== this._currentTime || duration !== this._duration) {
      // Update using setState to notify listeners
      this.setState({ currentTime: seek, duration: duration })
    }

    // Schedule next frame if still playing
    if (this._isPlaying) {
      // Double check ensures stopRaf wasn't called concurrently
      this.rafId = requestAnimationFrame(this.tick)
    }
  }

  private startRaf() {
    this.stopRaf() // Prevent duplicate loops
    console.debug('Starting RAF loop for time updates.')
    this.rafId = requestAnimationFrame(this.tick)
  }

  private stopRaf() {
    if (this.rafId) {
      console.debug('Stopping RAF loop.')
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
  }

  // --- Error Handling ---
  private handleError(src: string | null, context: string, error: any) {
    console.error(`${context} for ${src || 'unknown'}:`, error)
    if (this.currentSrc === src) {
      this.stopRaf()
      this.setState({
        isLoading: false,
        isPlaying: false,
        currentSrc: null,
        currentTime: 0,
        duration: 0,
      })
      this.currentAudio?.unload()
      this.currentAudio = null
      this.currentSrc = null
    }
  }
}

export default AudioManager
